import React, { useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '../lib/utils';
import {
  EDITABLE_ENTITY_RELEVANCE_LEVELS,
  clampEntityRelevance,
  entityRelevanceLevelLabelKey,
  getEntityRelevanceLevel,
  relevanceScoreForLevel,
  type EntityRelevanceLevel,
} from '../utils/entity-relevance';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

interface EntityRelevanceEditorProps {
  value?: number | null;
  disabled?: boolean;
  onChange: (nextRelevance: number | undefined) => void;
}

export const EntityRelevanceEditor: React.FC<EntityRelevanceEditorProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const relevance = clampEntityRelevance(value ?? undefined);
  const level = getEntityRelevanceLevel(relevance);
  const options = useMemo(
    () =>
      EDITABLE_ENTITY_RELEVANCE_LEVELS.map((optionLevel) => ({
        value: optionLevel,
        label: t(entityRelevanceLevelLabelKey(optionLevel)),
      })),
    [t]
  );

  if (disabled) {
    return <EntityRelevanceDisplay value={relevance} size="detailed" />;
  }

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex max-w-full">
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'group inline-flex max-w-full items-center gap-1 rounded border border-transparent px-1 py-0.5 -mx-1',
                  'cursor-pointer transition-[border-color,background-color] duration-150',
                  'hover:border-muted-foreground hover:bg-muted/40',
                  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30'
                )}
              >
                <EntityRelevanceDisplay value={relevance} size="detailed" showTooltip={false} />
                <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            </PopoverTrigger>
          </span>
        </TooltipTrigger>
        {!open ? (
          <TooltipContent side="top">{t('inline_editable_click_to_edit')}</TooltipContent>
        ) : null}
      </Tooltip>
      <PopoverContent
        data-entity-relevance-picker=""
        className="z-[2010] w-44 p-1"
        align="start"
        sideOffset={6}
      >
        <div className="flex flex-col gap-0.5">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(
                'flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
                option.value === level && 'bg-accent'
              )}
              onClick={() => {
                onChange(relevanceScoreForLevel(option.value as EntityRelevanceLevel));
                setOpen(false);
              }}
            >
              <EntityRelevanceDisplay
                value={relevanceScoreForLevel(option.value as EntityRelevanceLevel)}
                size="compact"
                showTooltip={false}
              />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
